import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import CardContent from '@material-ui/core/CardContent';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import PhoneAnchor from 'bento-ordering/base/components/PhoneAnchor';
import AddressLink from 'bento-ordering/base/components/AddressLink';
import LocationsPaper from 'bento-ordering/locations/ui_components/LocationsPaper';
import StaticMapComponent from 'bento-ordering/base/components/StaticMapComponent';
import Cover from 'bento-ordering/menus/ui_components/Cover';
import Flex from 'bento-ordering/base/ui_components/Flex';
import LocationCardDineInFulfillmentMethods from 'bento-ordering/locations/components/LocationCardDineInFullfilmentMethods';
import LocationCardOnlineOrderingFulfillmentMethods from 'bento-ordering/locations/components/LocationCardOnlineOrderingFulfillmentMethods';

import getApplicationVariant, {
  APPLICATION_VARIANTS
} from 'bento-ordering/utils/getApplicationVariant';

const MAP_BOX_MIN_WIDTH = '146px';
const MAP_BOX_MIN_HEIGHT = '136px';
const useCardMapBoxStyles = makeStyles({
  root: {
    position: 'relative',
    minHeight: MAP_BOX_MIN_HEIGHT,
    minWidth: MAP_BOX_MIN_WIDTH
  }
});

const useGridWithDividerStyles = makeStyles(theme => ({
  root: {
    display: 'block',
    [theme.breakpoints.up('md')]: {
      borderLeft: `1px solid ${theme.palette.borders.primary}`,
      paddingLeft: '20px',
      display: 'flex',
      justifyContent: 'space-between'
    }
  }
}));

const useCardContentStyles = makeStyles(theme => ({
  root: {
    display: 'block',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexGrow: 1,
      height: '100%'
    }
  }
}));

const useCardLocationSectionStyles = makeStyles({ root: { flexGrow: 2 } });

const LocationsCard = props => {
  const {
    name,
    description,
    distance,
    street,
    city,
    state,
    postalCode,
    phoneNumber,
    googlePlaceId,
    isSelected,
    lat,
    lng
  } = props;
  const mapBoxClasses = useCardMapBoxStyles();
  const gridWithDividerClasses = useGridWithDividerStyles();
  const cardContentClasses = useCardContentStyles();
  const cardLocationSectionStyles = useCardLocationSectionStyles();

  const theme = useTheme();
  const isMediumAndUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSmallAndUp = useMediaQuery(theme.breakpoints.up('sm'));
  const applicationVariant = getApplicationVariant();

  return (
    <LocationsPaper isSelected={isSelected}>
      <Flex>
        <div
          className={mapBoxClasses.root}
          style={isMediumAndUp ? {} : { minHeight: '50px' }}
        >
          <Cover style={isMediumAndUp ? {} : { position: 'relative' }}>
            <StaticMapComponent
              class={mapBoxClasses.root}
              locations={[{ lat, lng }]}
              center={{ lat, lng }}
              name={name}
              isMediumAndUp={isMediumAndUp}
              isSmallAndUp={isSmallAndUp}
            />
          </Cover>
        </div>

        <CardContent classes={cardContentClasses}>
          <div className={cardLocationSectionStyles.root}>
            <Box mb={0.75} display="flex">
              <Typography variant="h2">{name}</Typography>
              {distance && (
                <Box display="flex" alignItems="flex-end" ml={1}>
                  <Typography>({distance})</Typography>
                </Box>
              )}
            </Box>

            {street && city && (
              <Box mb={0.25}>
                <AddressLink
                  street={street}
                  city={city}
                  state={state}
                  postalCode={postalCode}
                  googlePlaceId={googlePlaceId}
                  dataTestId="location-address"
                />
              </Box>
            )}

            {phoneNumber && (
              <Box mb={0.75}>
                <PhoneAnchor number={phoneNumber} dataTestId="location-phone" />
              </Box>
            )}
            {description && (
              <Box mt={1} mb={0.5}>
                <Typography>{description}</Typography>
              </Box>
            )}
          </div>
          <div className={gridWithDividerClasses.root}>
            {applicationVariant === APPLICATION_VARIANTS.ONLINE_ORDERING && (
              <LocationCardOnlineOrderingFulfillmentMethods {...props} />
            )}

            {applicationVariant === APPLICATION_VARIANTS.DINE_IN && (
              <LocationCardDineInFulfillmentMethods {...props} />
            )}
          </div>
        </CardContent>
      </Flex>
    </LocationsPaper>
  );
};

LocationsCard.propTypes = {
  isSelected: PropTypes.bool,
  isClosed: PropTypes.bool,
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  distance: PropTypes.string,
  street: PropTypes.string.isRequired,
  city: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  postalCode: PropTypes.string.isRequired,
  phoneNumber: PropTypes.string,
  googlePlaceId: PropTypes.string,
  beginOrder: PropTypes.func.isRequired,
  isAcceptingPickupOrders: PropTypes.bool,
  isAcceptingDeliveryOrders: PropTypes.bool,
  openDisplayPickupHours: PropTypes.object.isRequired,
  openDisplayDeliveryHours: PropTypes.object.isRequired,
  id: PropTypes.number.isRequired,
  isAsapPickupCurrentlyAvailable: PropTypes.bool,
  isAsapDeliveryCurrentlyAvailable: PropTypes.bool,
  firstAvailablePickupDateOption: PropTypes.string,
  firstAvailableDeliveryDateOption: PropTypes.string,
  lat: PropTypes.string.isRequired,
  lng: PropTypes.string.isRequired,
  doesDeliverToAddress: PropTypes.bool
};

LocationsCard.defaultProps = {
  firstAvailablePickupDateOption: '',
  firstAvailableDeliveryDateOption: '',
  googlePlaceId: '',
  phoneNumber: '',
  distance: '',
  description: '',
  name: '',
  isClosed: false,
  isSelected: false,
  isAsapPickupCurrentlyAvailable: true,
  isAsapDeliveryCurrentlyAvailable: true,
  doesDeliverToAddress: true,
  isAcceptingPickupOrders: true,
  isAcceptingDeliveryOrders: true
};

export default LocationsCard;
